import React, { createContext, useContext, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';
import { useTranslation } from "react-i18next";

export type NotificationSeverity = 'success' | 'error' | 'info' | 'warning';

type NotificationContextType = {
    notify: (message: string, severity?: NotificationSeverity) => void;
};

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

type NotificationProviderProps = {
    children: React.ReactNode;
}

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState<NotificationSeverity>('info');

    const notify = (msg: string, type: NotificationSeverity = 'info') => {
        setMessage(msg);
        setSeverity(type);
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    return (
      <NotificationContext.Provider value={{ notify }}>
          {children}
          <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center'}} open={open} autoHideDuration={3000} onClose={handleClose}>
              <Alert onClose={handleClose} severity={severity} sx={{ width: '100%' }}>
                  {t(message)}
              </Alert>
          </Snackbar>
      </NotificationContext.Provider>
    );
};

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};
